import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import toast from "react-hot-toast"

import { useTranslation } from "react-i18next"
import {
  X,
  User,
  Mail,
  Phone,
  Building,
  Globe,
  AlertCircle,
  FileText,
  Loader2,
  Save,
  Tag,
  DollarSign,
  Hash,
  Bell,
  BellRing,
} from "lucide-react"
import { SearchableSelect, InputCustom, DateTimePicker } from "../../components"
import clientsService from "../../services/clientsService"
import { countryOptions } from '../../utils/data/CountryDialCodes'

// "YYYY-MM-DDTHH:MM" in the user's local timezone — the value format <input type="datetime-local"> expects.
const nowDatetimeLocal = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
};

const toDatetimeLocal = (raw) => {
  if (!raw) return nowDatetimeLocal();
  const d = new Date(raw);
  if (isNaN(d.getTime())) return nowDatetimeLocal();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 16);
};
const MONTHLY_SPEND_OPTIONS_RAW = [
  { id: 1, name: "$0 - $50K", value: "$0 - $50K" },
  { id: 2, name: "$50K - $100K", value: "$50K - $100K" },
  { id: 3, name: "$100K - $200K", value: "$100K - $200K" },
  { id: 4, name: "$200K - $300K", value: "$200K - $300K" },
  { id: 5, name: "$300K - $400K", value: "$300K - $400K" },
  { id: 6, name: "$400K - $500K", value: "$400K - $500K" },
  { id: 7, name: "$500K - $1M", value: "$500K - $1M" },
  { id: 8, name: "$1M+", value: "$1M+" },
];

const CATEGORY_OPTIONS_RAW = [
  { id: 1, name: "White Hat (Compliant Ads)", value: "White Hat (Compliant Ads)" },
  { id: 2, name: "Grey Hat (High-Risk Ads)", value: "Grey Hat (High-Risk Ads)" },
  { id: 3, name: "Black Hat (Non-Compliant Ads)", value: "Black Hat (Non-Compliant Ads)" },
  { id: 4, name: "Other", value: "Other" },
];

const KNOWN_CATEGORIES = [
  "White Hat (Compliant Ads)",
  "Grey Hat (High-Risk Ads)",
  "Black Hat (Non-Compliant Ads)",
];

const ClientFormModal = ({ isOpen, onClose, clientId = null, isEditing = false, onSuccess }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [logoPreview, setLogoPreview] = useState(null)

  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    businessName: "",
    website: "",
    userType: "advertiser",
    status: "active",
    country: "",
    notes: "",
    clientId: "",
    reference: '',
    telegram: '',
    sendWelcomeEmail: false,
    monthlySpend: "",
    category: "",
    joinDate: nowDatetimeLocal(),
    receiveSystemNotifications: true,
    receiveEmailNotifications: true,
  })

  // Category dropdown state (separate from formData.category to support "Other")
  const [categoryChoice, setCategoryChoice] = useState("")
  const [customCategory, setCustomCategory] = useState("")

  // Form validation
  const [errors, setErrors] = useState({})

  // Client type options
  const clientTypeOptions = [
    { id: 1, name: t("Advertiser"), value: "advertiser" },
    { id: 2, name: t("Agency"), value: "agency" },
    { id: 3, name: t("Partner"), value: "partner" },
    { id: 4, name: t("Reseller"), value: "reseller" },
    { id: 5, name: t("Affiliate"), value: "affiliate" },
  ]

  // Status options
  const statusOptions = [
    { id: 1, name: t("Active"), value: "active" },
    { id: 2, name: t("Inactive"), value: "inactive" },
    { id: 3, name: t("Pending"), value: "pending" },
    { id: 4, name: t("Suspended"), value: "suspended" },
    { id: 5, name: t("Frozen"), value: "frozen" },
    { id: 6, name: t("Blacklisted"), value: "blacklisted" },
  ]

  const monthlySpendOptions = MONTHLY_SPEND_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));
  const categoryOptions = CATEGORY_OPTIONS_RAW.map((o) => ({ ...o, name: t(o.name) }));

  // Fetch client data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (clientId && isEditing) {
        fetchClientData()
      } else {
        // Reset form for new client
        resetForm()
      }
    }
  }, [isOpen, clientId, isEditing])

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      businessName: "",
      website: "",
      userType: "advertiser",
      status: "active",
      country: "",
      notes: "",
      clientId: "",
      reference: '',
      telegram: '',
      sendWelcomeEmail: false,
      monthlySpend: "",
      category: "",
      joinDate: nowDatetimeLocal(),
      receiveSystemNotifications: true,
      receiveEmailNotifications: true,
    })
    setCategoryChoice("")
    setCustomCategory("")
    setLogoPreview(null)
    setErrors({})
  }

  // Fetch client data
  const fetchClientData = async () => {
    try {
      setLoading(true)
      const response = await clientsService.get(clientId)

      if (response.data && response.data.statusCode === 200) {
        const client = response.data.data

        setFormData({
          fullName: client.fullName || "",
          email: client.email || "",
          phone: client.phone || "",
          businessName: client.businessName || "",
          website: client.website || "",
          userType: client.userType || "advertiser",
          status: client.status || "active",
          country: client.country || "",
          notes: client.notes || "",
          clientId: client.clientId || "",
          reference: client.reference || "",
          telegram: client.telegram || "",
          monthlySpend: client.monthlySpend || "",
          category: client.category || "",
          joinDate: toDatetimeLocal(client.joinDate),
          receiveSystemNotifications:
            typeof client.receiveSystemNotifications === "boolean"
              ? client.receiveSystemNotifications
              : true,
          receiveEmailNotifications:
            typeof client.receiveEmailNotifications === "boolean"
              ? client.receiveEmailNotifications
              : true,
        })

        const cat = client.category || ""
        if (cat && !KNOWN_CATEGORIES.includes(cat)) {
          setCategoryChoice("Other")
          setCustomCategory(cat)
        } else {
          setCategoryChoice(cat)
          setCustomCategory("")
        }

        if (client.image) {
          setLogoPreview(client.image)
        }
      }
    } catch (error) {
      toast.error(t("Failed to load client data"))
      console.error("Client fetch failed:", error)
    } finally {
      setLoading(false)
    }
  }

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }))
    }
  }

  // Category dropdown handlers
  const handleCategoryChoice = (v) => {
    setCategoryChoice(v)
    if (v === "Other") {
      handleChange("category", customCategory)
    } else {
      setCustomCategory("")
      handleChange("category", v)
    }
  }

  const handleCustomCategoryChange = (v) => {
    setCustomCategory(v)
    handleChange("category", v)
  }

  // Validate form — keep in sync with the server's required fields:
  // create requires email, phone, userType, country, website, monthlySpend,
  // category; update requires reference, email, phone.
  const validateForm = () => {
    const newErrors = {}

    if (!formData.reference.trim()) {
      newErrors.reference = t("Client reference is required")
    }

    if (!formData.email.trim()) {
      newErrors.email = t("Email is required")
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("Email is invalid")
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("Whatsapp number is required")
    }

    if (!formData.businessName.trim()) {
      newErrors.businessName = t("Business name is required")
    }

    if (!isEditing) {
      if (!formData.country) {
        newErrors.country = t("Country is required")
      }

      if (!formData.website.trim()) {
        newErrors.website = t("Website is required")
      }
    }

    if (!formData.userType) {
      newErrors.userType = t("Client type is required")
    }

    if (!formData.status) {
      newErrors.status = t("Status is required")
    }

    if (!formData.monthlySpend.trim()) {
      newErrors.monthlySpend = t("Monthly spend is required")
    }

    if (!formData.category) {
      newErrors.category = t("Category is required")
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setProcessing(true)

      let response

      if (clientId && isEditing) {
        response = await clientsService.update(clientId, formData)
      } else {
        // Create new client
        response = await clientsService.create(formData)
      }

      if (response.data && response.data.statusCode === 200) {
        toast.success(isEditing ? t("Client updated successfully") : t("Client created successfully"))

        if (onSuccess) {
          onSuccess()
        }

        onClose()
        resetForm()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t("An error occurred"))
      console.error("Client operation failed:", error)
    } finally {
      setProcessing(false)
    }
  }

  // Get platform icon
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "meta":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 48 48">
            <path
              fill="#0081fb"
              d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5	c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28	c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51	c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40	c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35	c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57	c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5	c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
            ></path>
            <linearGradient
              id="wSMw7pqi7WIWHewz2_TZXa_PvvcWRWxRKSR_gr1"
              x1="42.304"
              x2="13.533"
              y1="24.75"
              y2="24.75"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0081fb"></stop>
              <stop offset=".995" stopColor="#0064e1"></stop>
            </linearGradient>
            <path
              fill="url(#wSMw7pqi7WIWHewz2_TZXa_PvvcWRWxRKSR_gr1)"
              d="M4.918,15.456	C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967	c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362	c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333	c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754	c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
            ></path>
            <linearGradient
              id="wSMw7pqi7WIWHewz2_TZXb_PvvcWRWxRKSR_gr2"
              x1="7.635"
              x2="7.635"
              y1="32.87"
              y2="13.012"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0081fb"></stop>
              <stop offset=".995" stopColor="#0064e1"></stop>
            </linearGradient>
            <path
              fill="url(#wSMw7pqi7WIWHewz2_TZXb_PvvcWRWxRKSR_gr2)"
              d="M14.25,14.5	c-1.959,0-3.683,1.362-5.075,3.465C7.206,20.937,6,25.363,6,29.614c0,1.753-0.003,3.072,0.5,3.886l-3.84,2.813	C1.574,34.507,1,32.2,1,29.5c0-4.91,1.355-10.091,3.918-14.044C7.192,11.951,10.507,9.5,14.27,9.5L14.25,14.5z"
            ></path>
            <path
              d="M21.67,20.27l-0.3,0.45l-0.29,0.46c0.71,1.03,1.52,2.27,2.37,3.69l0.21-0.37c0.02-0.04,0.05-0.09,0.08-0.13 c0.09-0.17,0.19-0.34,0.28-0.51C23.19,22.5,22.39,21.29,21.67,20.27z M24.94,15.51c-0.11,0.14-0.21,0.28-0.31,0.42 c0.73,0.91,1.47,1.94,2.25,3.1c0.1-0.16,0.2-0.31,0.3-0.46c0.04-0.06,0.07-0.11,0.11-0.16c0.06-0.1,0.13-0.19,0.19-0.28 c-0.76-1.12-1.5-2.13-2.23-3.03C25.15,15.23,25.04,15.37,24.94,15.51z"
              opacity=".05"
            ></path>
            <path
              d="M21.67,20.27l-0.3,0.45c0.71,1.02,1.51,2.24,2.37,3.65c0.09-0.17,0.19-0.34,0.28-0.51C23.19,22.5,22.39,21.29,21.67,20.27 z M24.63,15.93c0.73,0.91,1.47,1.94,2.25,3.1c0.1-0.16,0.2-0.31,0.3-0.46c-0.77-1.14-1.52-2.16-2.24-3.06 C24.83,15.65,24.73,15.79,24.63,15.93z"
              opacity=".07"
            ></path>
          </svg>
        )
      case "google":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 48 48">
            <polygon fill="#ffc107" points="30.129,15.75 18.871,9.25 5.871,31.25 17.129,37.75"></polygon>
            <path
              fill="#1e88e5"
              d="M31.871,37.75c1.795,3.109,5.847,4.144,8.879,2.379c3.103-1.806,4.174-5.77,2.379-8.879l-13-22 c-1.795-3.109-5.835-4.144-8.879-2.379c-3.106,1.801-4.174,5.77-2.379,8.879L31.871,37.75z"
            ></path>
            <circle cx="11.5" cy="34.5" r="6.5" fill="#43a047"></circle>
          </svg>
        )
      case "tiktok":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="30" height="35" viewBox="0 0 48 48">
            <path
              fill="#212121"
              fillRule="evenodd"
              d="M10.904,6h26.191C39.804,6,42,8.196,42,10.904v26.191 C42,39.804,39.804,42,37.096,42H10.904C8.196,42,6,39.804,6,37.096V10.904C6,8.196,8.196,6,10.904,6z"
              clipRule="evenodd"
            ></path>
            <path
              fill="#ec407a"
              fillRule="evenodd"
              d="M29.208,20.607c1.576,1.126,3.507,1.788,5.592,1.788v-4.011 c-0.395,0-0.788-0.041-1.174-0.123v3.157c-2.085,0-4.015-0.663-5.592-1.788v8.184c0,4.094-3.321,7.413-7.417,7.413 c-1.528,0-2.949-0.462-4.129-1.254c1.347,1.376,3.225,2.23,5.303,2.23c4.096,0,7.417-3.319,7.417-7.413L29.208,20.607L29.208,20.607 z M30.657,16.561c-0.805-0.879-1.334-2.016-1.449-3.273v-0.516h-1.113C28.375,14.369,29.331,15.734,30.657,16.561L30.657,16.561z M19.079,30.832c-0.45-0.59-0.693-1.311-0.692-2.053c0-1.873,1.519-3.391,3.393-3.391c0.349,0,0.696,0.053,1.029,0.159v-4.1 c-0.389-0.053-0.781-0.076-1.174-0.068v3.191c-0.333-0.106-0.68-0.159-1.03-0.159c-1.874,0-3.393,1.518-3.393,3.391 C17.213,29.127,17.972,30.274,19.079,30.832z"
              clipRule="evenodd"
            ></path>
            <path
              fill="#fff"
              fillRule="evenodd"
              d="M28.034,19.63c1.576,1.126,3.507,1.788,5.592,1.788v-3.157 c-1.164-0.248-2.194-0.856-2.969-1.701c-1.326-0.827-2.281-2.191-2.561-3.788h-2.923v16.018c-0.007,1.867-1.523,3.379-3.393,3.379 c-1.102,0-2.081-0.525-2.701-1.338c-1.107-0.558-1.866-1.705-1.866-3.029c0-1.873,1.519-3.391,3.393-3.391 c0.359,0,0.705,0.056,1.03,0.159V21.38c-4.024,0.083-7.26,3.369-7.26,7.411c0,2.018,0.806,3.847,2.114,5.183 c1.18,0.792,2.601,1.254,4.129,1.254c4.096,0,7.417-3.319,7.417-7.413L28.034,19.63L28.034,19.63z"
              clipRule="evenodd"
            ></path>
            <path
              fill="#81d4fa"
              fillRule="evenodd"
              d="M33.626,18.262v-0.854c-1.05,0.002-2.078-0.292-2.969-0.848 C31.445,17.423,32.483,18.018,33.626,18.262z M28.095,12.772c-0.027-0.153-0.047-0.306-0.061-0.461v-0.516h-4.036v16.019 c-0.006,1.867-1.523,3.379-3.393,3.379c-0.549,0-1.067-0.13-1.526-0.362c0.62,0.813,1.599,1.338,2.701,1.338 c1.87,0,3.386-1.512,3.393-3.379V12.772H28.095z M21.635,21.38v-0.909c-0.337-0.046-0.677-0.069-1.018-0.069 c-4.097,0-7.417,3.319-7.417,7.413c0,2.567,1.305,4.829,3.288,6.159c-1.308-1.336-2.114-3.165-2.114-5.183 C14.374,24.749,17.611,21.463,21.635,21.38z"
              clipRule="evenodd"
            ></path>
          </svg>
        )
      case "snapchat":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 48 48">
            <path
              fill="#ffeb3b"
              d="M24,4C12.402,4,3,13.402,3,25c0,11.598,9.402,21,21,21s21-9.402,21-21C45,13.402,35.598,4,24,4z"
            ></path>
            <path
              fill="#fff"
              d="M24,36c-2.451,0-4.045-1.09-4.827-1.612c-0.477-0.317-0.924-0.616-1.317-0.705 c-0.345-0.078-0.685-0.118-1.012-0.118c-0.59,0-1.056,0.103-1.386,0.175c-0.207,0.045-0.385,0.084-0.526,0.084 c-0.143,0-0.298-0.032-0.367-0.267c-0.064-0.218-0.107-0.428-0.148-0.631c-0.116-0.568-0.198-0.92-0.416-0.954 c-2.562-0.401-3.295-0.945-3.464-1.331c-0.024-0.055-0.037-0.11-0.04-0.165c-0.008-0.138,0.09-0.258,0.227-0.283 c3.731-0.614,5.399-4.440,5.467-4.589c0.004-0.009,0.008-0.017,0.012-0.026c0.225-0.456,0.268-0.851,0.131-1.174 c-0.258-0.611-1.099-0.879-1.657-1.058c-0.137-0.044-0.268-0.086-0.371-0.126c-0.504-0.198-1.349-0.612-1.241-1.186 c0.078-0.416,0.615-0.707,1.049-0.707c0.12,0,0.226,0.021,0.316,0.063c0.518,0.242,0.986,0.364,1.392,0.364 c0.558,0,0.802-0.236,0.833-0.266c-0.015-0.273-0.033-0.557-0.051-0.846c-0.119-1.922-0.268-4.313,0.337-5.674 C18.915,13.058,22.304,13,23.104,13h0.792c0.8,0,4.189,0.058,6.254,3.347c0.605,1.359,0.456,3.75,0.337,5.673 c-0.018,0.29-0.036,0.573-0.051,0.847c0.031,0.03,0.275,0.266,0.833,0.266c0.406,0,0.874-0.122,1.392-0.364 c0.09-0.042,0.196-0.063,0.316-0.063c0.433,0,0.971,0.291,1.049,0.707c0.108,0.574-0.737,0.988-1.241,1.186 c-0.103,0.04-0.234,0.082-0.371,0.126c-0.558,0.179-1.399,0.447-1.657,1.058c-0.137,0.323-0.094,0.718,0.131,1.174 c0.004,0.009,0.008,0.017,0.012,0.026c0.068,0.149,1.736,3.975,5.467,4.589c0.137,0.025,0.235,0.145,0.227,0.283 c-0.003,0.055-0.016,0.11-0.04,0.165c-0.169,0.386-0.902,0.93-3.464,1.331c-0.218,0.034-0.224,0.267-0.367,0.267c-0.142,0-0.319-0.039-0.526-0.084 c-0.33-0.072-0.796-0.175-1.386-0.175c-0.327,0-0.667,0.04-1.012,0.118c-0.393,0.089-0.84,0.388-1.317,0.705 C28.045,34.91,26.451,36,24,36z"
            ></path>
          </svg>
        )
      default:
        return <Globe size={20} className="text-neutral-800 dark:text-neutral-400" />
    }
  }




  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-xl transition-all">
                {/* Header */}
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                      <Building className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                      {isEditing ? t("Update Client") : t("Add New Client")}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-6">
                        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                          {t("Basic Information")}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <InputCustom
                              label={`${t("Client Reference")}*`}
                              placeholder={t("Enter client reference")}
                              value={formData.reference}
                              onChange={(value) => handleChange("reference", value)}
                              icon={<User size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.reference}
                            />
                            {errors.reference && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.reference}
                              </p>
                            )}
                          </div>
                          {/* Client ID */}
                          <div className="space-y-1">
                            <InputCustom
                              label={t("Client ID")}
                              placeholder={t("Enter client ID")}
                              value={formData.clientId}
                              onChange={(value) => handleChange("clientId", value)}
                              icon={<Hash size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                          </div>

                          {/* Business Name */}
                          <div className="space-y-1">
                            <InputCustom
                              label={`${t("Company Name")}*`}
                              placeholder={t("Enter company name")}
                              value={formData.businessName}
                              onChange={(value) => handleChange("businessName", value)}
                              icon={<Building size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.businessName}
                            />
                            {errors.businessName && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.businessName}
                              </p>
                            )}
                          </div>


                          {/* Email */}
                          <div className="space-y-1">
                            <InputCustom
                              type="email"
                              label={`${t("Email Address")}*`}
                              placeholder={t("Enter email address")}
                              value={formData.email}
                              onChange={(value) => handleChange("email", value)}
                              icon={<Mail size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.email}
                            />
                            {errors.email && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.email}
                              </p>
                            )}
                          </div>

                          {/* Phone */}
                          <div className="space-y-1">
                            <InputCustom
                              type="tel"
                              label={`${t("Whatsapp Number")}*`}
                              placeholder={t("Enter whatsapp number")}
                              value={formData.phone}
                              onChange={(value) => handleChange("phone", value)}
                              icon={<Phone size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.phone}
                            />
                            {errors.phone && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.phone}
                              </p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <InputCustom
                              type="url"
                              label={t("Telegram")}
                              placeholder={t("Enter client telegram")}
                              value={formData.telegram}
                              onChange={(value) => handleChange("telegram", value)}
                              icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                            />
                          </div>

                          {/* Country */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Country")}*
                            </label>
                            <SearchableSelect
                              options={countryOptions}
                              value={formData.country}
                              onChange={(value) => handleChange("country", value)}
                              placeholder={t("Select country")}
                              icon={<Globe size={16} />}
                              error={errors.country}
                            />
                            {errors.country && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.country}
                              </p>
                            )}
                          </div>
                          {/* Website */}
                          <div className="space-y-1">
                            <InputCustom
                              type="url"
                              label={`${t("Website")}*`}
                              placeholder={t("Enter website URL")}
                              value={formData.website}
                              onChange={(value) => handleChange("website", value)}
                              icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.website}
                            />
                            {errors.website && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.website}
                              </p>
                            )}
                          </div>



                          {/* Full Name */}
                          {/* <div className="space-y-1">
                            <InputCustom
                              label={t("Full Name")}
                              placeholder={t("Enter full name")}
                              value={formData.fullName}
                              onChange={(value) => handleChange("fullName", value)}
                              icon={<User size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              error={errors.fullName}
                            />
                            {errors.fullName && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.fullName}
                              </p>
                            )}
                          </div> */}


                          {/* Password is intentionally not collected here — the
                              server always generates a random internal password
                              and the client sets their own via the welcome-email
                              magic link. */}
  {/* Join Date */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Join Date")}
                            </label>
                            <DateTimePicker
                              value={formData.joinDate}
                              onChange={(v) => handleChange("joinDate", v)}
                              placeholder={t("Select join date & time")}
                            />
                          </div>







                        </div>
                      </div>

                      <div className="border-t border-neutral-200 dark:border-neutral-900  pt-6 space-y-6">
                        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                          {t("Additional Details")}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Client Type */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Client Type")}*
                            </label>
                            <SearchableSelect
                              options={clientTypeOptions}
                              value={formData.userType}
                              onChange={(value) => handleChange("userType", value)}
                              placeholder={t("Select client type")}
                              icon={<Tag size={16} />}
                              error={errors.userType}
                            />
                            {errors.userType && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.userType}
                              </p>
                            )}
                          </div>

                          {/* Status */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Status")}*
                            </label>
                            <SearchableSelect
                              options={statusOptions}
                              value={formData.status}
                              onChange={(value) => handleChange("status", value)}
                              placeholder={t("Select status")}
                              icon={<AlertCircle size={16} />}
                              error={errors.status}
                            />
                            {errors.status && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.status}
                              </p>
                            )}

                          </div>
                          {/* Monthly Spend */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Monthly Spend")}*
                            </label>
                            <SearchableSelect
                              options={monthlySpendOptions}
                              value={formData.monthlySpend}
                              onChange={(v) => handleChange("monthlySpend", v)}
                              placeholder={t("Select monthly spend")}
                              icon={<DollarSign size={16} />}
                            />
                            {errors.monthlySpend && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.monthlySpend}
                              </p>
                            )}
                          </div>

                          {/* Category */}
                          <div className="space-y-1">
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                              {t("Category")}*
                            </label>
                            <SearchableSelect
                              options={categoryOptions}
                              value={categoryChoice}
                              onChange={handleCategoryChoice}
                              placeholder={t("Select category")}
                              icon={<Tag size={16} />}
                            />
                            {categoryChoice === "Other" && (
                              <InputCustom
                                placeholder={t("Enter custom category")}
                                value={customCategory}
                                onChange={handleCustomCategoryChange}
                                icon={<Tag size={16} className="text-neutral-500 dark:text-neutral-400" />}
                              />
                            )}
                            {errors.category && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                {errors.category}
                              </p>
                            )}
                          </div>

                        

                        </div>

                        {/* Notes */}
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("Notes")}
                          </label>
                          <div className="relative">
                            <div className="absolute top-3 left-3 pointer-events-none">
                              <FileText size={16} className="text-neutral-500 dark:text-neutral-400" />
                            </div>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => handleChange("notes", e.target.value)}
                              rows={3}
                              placeholder={t("Add any additional information about this client...")}
                              className="w-full pl-9 pt-2 pr-3 border text-sm border-neutral-300 dark:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            />
                          </div>
                        </div>
                        {/* Notification Preferences */}
                        <div className="space-y-3 mt-6">
                          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
                            {t("Notification Preferences")}
                          </h3>

                          <div className="flex items-center space-x-3 p-3 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:border-neutral-800/50">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                                <Bell size={16} className="text-neutral-500 dark:text-neutral-400" />
                                {t("Receive System Notifications")}
                              </h4>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                {t("Show in-app notifications to this client")}
                              </p>
                            </div>
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.receiveSystemNotifications}
                                onChange={(e) => handleChange("receiveSystemNotifications", e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="relative w-11 h-6 bg-neutral-200 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-blue-600" />
                            </label>
                          </div>

                          <div className="flex items-center space-x-3 p-3 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:border-neutral-800/50">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                                <BellRing size={16} className="text-neutral-500 dark:text-neutral-400" />
                                {t("Receive Email Notifications")}
                              </h4>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                {t("Send notification emails to this client")}
                              </p>
                            </div>
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.receiveEmailNotifications}
                                onChange={(e) => handleChange("receiveEmailNotifications", e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="relative w-11 h-6 bg-neutral-200 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-blue-600" />
                            </label>
                          </div>
                        </div>

                        {
                          !isEditing &&
                          <div className="flex items-center mt-6">
                            <input
                              id="send-welcome"
                              type="checkbox"
                              checked={formData.sendWelcomeEmail}
                              onChange={(e) => handleChange("sendWelcomeEmail", !formData.sendWelcomeEmail)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 rounded"
                            />
                            <label
                              htmlFor="send-welcome"
                              className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300"
                            >
                              {t("Send welcome email to client")}
                            </label>
                          </div>
                        }

                      </div>
                    </form>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-xl hover:bg-neutral-50 dark:bg-transparent dark:text-neutral-300 dark:border-neutral-600 dark:hover:bg-neutral-800 focus:outline-none"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={processing || loading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black hover:scale-105 transition-all duration-300 rounded-xl  focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("Processing...")}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {isEditing ? t("Update Client") : t("Save Client")}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ClientFormModal
